import {
  Id,
  Identifiable,
  resource as resourceMock,
  project as projectMock,
  list as listMock,
  task as taskMock,
  TimeSheet,
  TimeSheetFilter,
  Resource,
  Project,
  Task,
  TimeRecord,
  allocation,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";

type ResponseList = {
  uuid: Id;
  name: string;
  nbrComments: number;
  spentTimes: { [key: string]: number };
  nbrAllocatedHours: number;
  totalOfSpentTime: number;
  totalOfSpentTimeInWeek: number;
  totalOfRemainingTime: number;
  percentComplete: number;
  subActivities: ResponseList[];
  tasks: {
    uuid: Id;
    name: string;
    spentTimes: { [key: string]: number };
    nbrAllocatedHours: number;
    totalOfSpentTime: number;
    totalOfSpentTimeInWeek: number;
    totalOfRemainingTime: number;
    timeRecords: {
      uuid: Id;
      date: number;
      month: number;
      year: number;
      timeSpent: number;
      attributes?: { [key: string]: string };
      timeSpentInWeek: number;
    }[];
    taskResourceLatestRecord: string;
    taskResourceLatestRecordingDay: string;
    resourcePercentComplete: number;
    allocationData: {
      allocationUuid: string;
      resourceUuid: string;
    }[];
  }[];
};

type Response = {
  uuid: Id;
  name: string;
  profession: string;
  totalOfAllocatedHours: number;
  totalOfSpentTime: number;
  totalOfSpentTimeInWeek: number;
  totalOfRemainingTime: number;
  spentTimes: { [key: string]: number };
  percentCompletion: number;
  unavailabilities: { [key: string]: boolean };
  projects: {
    uuid: Id;
    totalOfAllocatedHours: number;
    totalOfSpentTime: number;
    totalOfSpentTimeInWeek: number;
    totalOfRemainingTime: number;
    percentComplete: number;
    spentTimes: { [key: string]: number };
    activityRoot: ResponseList;
  }[];
};

function isUnAvailable(
  at: string,
  unAvailabilities: { [key: string]: boolean },
): boolean {
  return unAvailabilities[at];
}

function responseToSpentTimes(
  res: { [key: string]: number },
  unAvailabilities: { [key: string]: boolean },
): { date: string; time: number; isAvailable: boolean }[] {
  return Object.entries(res)
    .map(([date, time]) => ({
      date,
      time,
      isAvailable: !isUnAvailable(date, unAvailabilities),
    }))
    .sort((first, second) =>
      new Date(first.date) < new Date(second.date) ? -1 : 1,
    );
}

function formatList(
  list: ResponseList,
  project: Identifiable<Project>,
  resource: Identifiable<Resource> & {
    unavailabilities: { [key: string]: boolean };
  },
): TimeSheet["resources"][number]["lists"][number] {
  return {
    ...listMock,
    ...list,
    percentComplete: list.percentComplete,
    project: { ...projectMock, ...project },
    subLists: list.subActivities.map((subList) =>
      formatList(subList, project, resource),
    ),
    spentTimes: responseToSpentTimes(
      list.spentTimes,
      resource.unavailabilities,
    ),
    tasks: list.tasks.map((task) => ({
      ...taskMock,
      ...task,
      allocations: task.allocationData.map((allocation) => ({
        uuid: allocation.allocationUuid,
        allocatedHours: task.nbrAllocatedHours,
        percentComplete: task.resourcePercentComplete,
        estimatedAt: 0,
        completedAt: "",
        resource: { ...resource, uuid: allocation.resourceUuid },
      })),
      spentTimes: responseToSpentTimes(
        task.spentTimes,
        resource.unavailabilities,
      ),
      timeRecords: task.timeRecords.map((record) => ({
        ...record,
        task: { ...taskMock, ...task },
        resource,
      })),
    })),
  };
}

function responseToTimeSheet(resource: Response): TimeSheet {
  return {
    spentTimes: responseToSpentTimes(
      resource.spentTimes,
      resource.unavailabilities,
    ),
    resources: [
      {
        ...resourceMock,
        ...resource,
        spentTimes: responseToSpentTimes(
          resource.spentTimes,
          resource.unavailabilities,
        ),
        lists: resource.projects.map((project) => ({
          ...listMock,
          ...project,
          project: { ...projectMock, ...project },
          nbrAllocatedHours: project.totalOfAllocatedHours,
          subLists: project.activityRoot.subActivities.map((subList) =>
            formatList(
              subList,
              { ...projectMock, ...project },
              { ...resourceMock, ...resource },
            ),
          ),
          spentTimes: responseToSpentTimes(
            project.spentTimes,
            resource.unavailabilities,
          ),
          tasks: project.activityRoot.tasks.map((task) => ({
            ...taskMock,
            ...task,
            allocations: task.allocationData.map((allocation) => ({
              allocatedHours: task.nbrAllocatedHours,
              percentComplete: task.resourcePercentComplete,
              resource: { ...resourceMock, uuid: resource.uuid },
              estimatedAt: 0,
              completedAt: "",
              uuid: allocation.allocationUuid,
            })),
            spentTimes: responseToSpentTimes(
              task.spentTimes,
              resource.unavailabilities,
            ),
            timeRecords: task.timeRecords.map((record) => ({
              ...record,
              task: { ...taskMock, ...task },
              resource: { ...resourceMock, ...resource },
            })),
          })),
        })),
      },
    ],
  };
}

function getTimeSheet(httpClient: HttpClient) {
  return (
    resource: Identifiable<Resource>,
    record: TimeRecord,
    filter: TimeSheetFilter,
  ) => {
    return httpClient
      .sendHttpRequest<Response>({
        endpoint: `/resources/${resource.uuid}/task/${record.task.uuid}/timesheet/by-week`,
        method: HttpMethods.GET,
        headers: {
          startDate: filter.minDate,
          endDate: filter.maxDate,
        },
      })
      .then(responseToTimeSheet);
  };
}

export { getTimeSheet };
